/* eslint-disable react/jsx-no-constructed-context-values */
/* eslint-disable consistent-return */
import axios from 'axios';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AppCustomCode, WhiteLabelData, WLFacebookIntegration, WLSlackIntegration } from '@/models/api/response.types';
import { hexToHSL } from '@/utils/colors';
import {
  currentPrimaryDomain,
  isDev,
  isDevDomain,
  isPrimaryProduct,
  isPrimaryProductByDomain,
} from '@/utils/domain';
import { getLicenseeApiUrl, getLicenseeDomainOverride, getPrimaryDomain } from '@/config';

export interface WhiteLabelContextProps {
  primaryColor: string;
  secondaryColor: string;
  licenseeUUID: string;
  appEmail: string;
  appLogo: string;
  appTitle: string;
  mainWebsite: string;
  appFreePlanKeys: any;
  appPrivacyPolicyLink: string;
  appTermsOfServiceLink: string;
  documentationLink: string;
  appDomain: string;
  rewardfulAPIKey: string;
  disableBilling: boolean;
  disableOnboarding: boolean;
  disableChatbotCreation: boolean;
  zapierIntegrationLink: string;
  makeIntegrationLink: string;
  canUseURLScrapper: boolean;
  facebookIntegration?: WLFacebookIntegration;
  slackIntegration?: WLSlackIntegration;
  enterprisePlan?: WhiteLabelData['app']['enterprise_plan'];
  canSeeSignUpFlow: boolean;
  defaultChatbotAIModel: string;
  fetchAppData: () => void;
  customCodes: AppCustomCode[];
  sourceCopyRightDisclaimer: NonNullable<WhiteLabelData['app']['sources']>['copyright_disclaimer'];
  enableCustomSignUpAgreement: boolean;
  customSignUpAgreementText: string;
}

const WhiteLabelContext = createContext<WhiteLabelContextProps | undefined>(undefined);

const WhiteLabelProvider: React.FC<{ isHostedByWidget?: boolean; children: ReactNode }> = ({
  isHostedByWidget = false,
  children,
}) => {
  const [data, setData] = useState<WhiteLabelData | undefined>(undefined);
  const [domainSetUpCompleted, setDomainSetupCompleted] = useState<boolean>(false);

  const fetchAppData = useCallback(async () => {
    // for chat widget we need to use GPTTConfig domain param
    // that users are passing in order to make it work
    const baseUrl = getLicenseeApiUrl();
    
    // Determine domain to look up in licensee API
    let domainToLookUp: string;
    if (isHostedByWidget) {
      // Widget: use GPTTConfig domain or primary domain from config
      domainToLookUp = window.GPTTConfig?.domain || getPrimaryDomain();
    } else {
      // Check for local dev override (e.g., when connecting to staging from localhost)
      const domainOverride = getLicenseeDomainOverride();
      if (domainOverride) {
        domainToLookUp = domainOverride;
      } else {
        // Normal operation: use window.location.hostname (supports custom domains)
        domainToLookUp = window.location.hostname;
      }
    }
    
    const licenseeUrl = `${baseUrl}/api/licensee/by_domain?domain=${domainToLookUp}`;

    try {
      // hiding cache because it's bad .... not secure
      // const cachedData = sessionStorage.getItem('platform_whitelabel_data');
      let jsonData;

      // if (cachedData) {
      //   jsonData = JSON.parse(cachedData);
      // } else {
      const response = await fetch(licenseeUrl);
      if (!response.ok) {
        jsonData = {};
        sessionStorage.setItem('platform_whitelabel_data', JSON.stringify(jsonData));
        throw new Error('Network response was not ok');
      }
      jsonData = await response.json();
      // sessionStorage.setItem('platform_whitelabel_data', JSON.stringify(jsonData));
      // }
      setData(jsonData);

      axios.defaults.headers.post['Content-Type'] = 'application/json';
      axios.defaults.baseURL = isPrimaryProductByDomain(jsonData.app_domain)
        ? `https://${currentPrimaryDomain}`
        : `https://${jsonData.app_domain}`;

      setDomainSetupCompleted(true);
      const primaryColor = hexToHSL(jsonData.app.primary_color);
      const primarySecondaryColor = hexToHSL(jsonData?.app?.primary_secondary_color || '#d24b52');
      const { l: luminosity } = hexToHSL(jsonData.app.primary_color);
      const textPrimaryColor = luminosity < 60 ? hexToHSL('#fff') : hexToHSL('#303030');
      const secondaryColor = hexToHSL(jsonData.app.secondary_color);
      const secondaryBackground = hexToHSL(jsonData.app.background_color);

      document.body.style.setProperty(
        '--primary-secondary',
        `${primarySecondaryColor.h} ${primarySecondaryColor.s}% ${primarySecondaryColor.l}%`,
      );
      document.body.style.setProperty('--primary', `${primaryColor.h} ${primaryColor.s}% ${primaryColor.l}%`);
      document.body.style.setProperty(
        '--text-primary',
        `${textPrimaryColor.h} ${textPrimaryColor.s}% ${textPrimaryColor.l}%`,
      );
      document.body.style.setProperty(
        '--main',
        `${secondaryColor.h} ${secondaryColor.s}% ${secondaryColor.l}%`,
      );
      document.body.style.setProperty(
        '--secondary-background',
        `${secondaryBackground.h} ${secondaryBackground.s}% ${secondaryBackground.l}%`,
      );
    } catch (error) {
      console.error('There was a problem with getting whitelabel data:', error);
    }
  }, []);

  useEffect(() => {
    fetchAppData();
  }, []);

  const contextValue = useMemo(() => {
    return {
      licenseeUUID: data?.uuid || '',
      appLogo: data?.logo_url || '',
      appEmail: data?.app?.email || '',
      appTitle: data?.app?.name || '',
      appPrivacyPolicyLink: data?.app?.privacy_policy_link || '',
      appTermsOfServiceLink: data?.app?.term_of_service_link || '',
      documentationLink: data?.app?.documentation_link || '',
      mainWebsite: data?.app?.website_url || '',
      appFreePlanKeys: data?.free_plan,
      secondaryColor: data?.app?.secondary_color || '#bfdbfe',
      primaryColor: data?.app?.primary_color || '#404258',
      secondaryPrimaryColor: data?.app?.secondary_primary_color || '#d24b52',
      appDomain: data?.app_domain || 'app.gpt-trainer.com',
      rewardfulAPIKey: data?.meta?.rewardful_key || '',
      sourceCopyRightDisclaimer: data?.app?.sources?.copyright_disclaimer,
      canSeeSignUpFlow: !data?.app?.authorization?.disable_sign_up,
      defaultChatbotAIModel:
        data?.app?.chatbots?.default_ai_model || (isPrimaryProduct ? 'o3-mini-16k' : 'gpt-4o-mini-4k'), //for partners 4o-mini-4k
      canUseURLScrapper:
        data?.app?.sources?.website_scraping_enabled !== undefined
          ? data?.app?.sources?.website_scraping_enabled
          : true,
      zapierIntegrationLink: data?.meta?.integrations?.zapier_url || '',
      makeIntegrationLink: data?.meta?.integrations?.make_url || '',
      facebookIntegration: data?.meta?.integrations?.facebook,
      slackIntegration: data?.meta?.integrations?.slack,
      customCodes: data?.app?.custom_code || [],
      disableBilling: data?.app?.billing?.disabled || false,
      disableOnboarding: data?.app?.app?.disable_onboarding || false,
      disableChatbotCreation: data?.app?.chatbots?.disable_creation || false,
      enterprisePlan: data?.app?.enterprise_plan || undefined,
      enableCustomSignUpAgreement: data?.app?.sign_up?.enable_custom_aggreement || false,
      customSignUpAgreementText: data?.app?.sign_up?.custom_aggreement_text || '',
    };
  }, [data]);

  if (!domainSetUpCompleted) {
    return null;
  }

  return (
    <WhiteLabelContext.Provider value={{ ...contextValue, fetchAppData }}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

const useWhitelabelData = (): WhiteLabelContextProps => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error('useWhitelabelData must be used within a WhiteLabelProvider');
  }
  return context;
};

export { WhiteLabelProvider, useWhitelabelData };
